import { FC, FormEvent, useEffect, useState } from 'react';

import { Button, Container } from '@purpur/library';
import kebabCase from 'lodash/kebabCase';

import { IMAGES_PATH } from '@telialt/assets/src/constants';
import { useTranslations } from '@telialt/common-cms/src/useTranslations';

import { LeadFormType } from '../../../../models/Content';
import { BlockSection } from '../../../common/Common.styles';
import { LEAD_TEST_IDS } from '../../constants';
import { useGAEvent } from '../../hooks/useGAEvent';
import { useLeadSubmit } from '../../hooks/useLeadSubmit';
import { LeadChildrenProps, LeadFormData } from '../../model';
import { LeadPromotionCard, PromotionCardBody } from './LeadFormBlock.styles';

type Props = LeadChildrenProps &
    Pick<LeadFormType['fields'], 'name' | 'submitButtonLabel' | 'legalText' | 'commentHint'>;

export const LeadFormBlock: FC<Props> = ({
    leadFormContent,
    successNotification,
    errorNotification,
    name,
    submitButtonLabel,
    legalText,
    commentHint,
}) => {
    const { t } = useTranslations();
    const { submitLead, resetSubmitLead, isPending, isSuccess, isError } = useLeadSubmit();

    const retryButton = (
        <Button variant="secondary" type="button" onClick={resetSubmitLead} data-test={LEAD_TEST_IDS.RETRY_BUTTON}>
            {t['content.lead.retry_button']}
        </Button>
    );
    const submitButton = (
        <Button variant="primary" type="submit" loading={isPending} data-test={LEAD_TEST_IDS.SUBMIT_BUTTON}>
            {submitButtonLabel}
        </Button>
    );
    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (event.target instanceof HTMLFormElement) {
            const formData: LeadFormData = Object.fromEntries(new FormData(event.target));
            submitLead(
                { ...formData, legalText, comment: formData.comment || commentHint },
                {
                    onSuccess: () =>
                        pushGAEvent('formSubmissionSuccess', {
                            phone: formData.phoneNumber,
                            email: formData.emailAddress,
                        }),
                },
            );
        }
    };

    const { isLoading, pushGAEvent } = useGAEvent();
    const [isDirty, setIsDirty] = useState(false);
    const markFormAsDirty = () => {
        if (!isDirty) {
            setIsDirty(true);
            pushGAEvent('formStart');
        }
    };

    useEffect(() => {
        if (!isLoading) {
            pushGAEvent('formOpen');
        }
    }, [isLoading, pushGAEvent]);

    return (
        <BlockSection data-section="Lead Form Block" data-section-name={name} id={kebabCase(name)}>
            <Container>
                <form onSubmit={handleSubmit} onChange={markFormAsDirty} data-test={LEAD_TEST_IDS.LEAD_FORM}>
                    <LeadPromotionCard
                        title={name}
                        headingTag="h2"
                        headingVariant="title-300"
                        variant="quaternary"
                        alignment="flex-end"
                        enableTitleHyphenation={false}
                        actions={isSuccess || isError ? retryButton : submitButton}
                        decorativeImage={
                            <img src={`${IMAGES_PATH}/temp/leads-form-banner3.png`} alt="" role="presentation" />
                        }
                    >
                        <div hidden={isSuccess || isError}>{leadFormContent}</div>
                        {isSuccess && <PromotionCardBody>{successNotification}</PromotionCardBody>}
                        {isError && <PromotionCardBody>{errorNotification}</PromotionCardBody>}
                    </LeadPromotionCard>
                </form>
            </Container>
        </BlockSection>
    );
};
